import { useState, useMemo, useEffect } from 'react';
import {
  Table,
  Button,
  Input,
  Select,
  Space,
  Typography,
  Modal,
  Form,
  InputNumber,
  Tag,
  Switch,
} from 'antd';
import { PlusOutlined, EditOutlined, UploadOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import {
  useCustomers,
  useCustomerById,
  useCreateCustomer,
  useUpdateCustomer,
} from '@hooks/useSales';
import { useImportCustomers } from '@hooks/useImport';
import type {
  CustomerListItem,
  CustomerDetail,
  CustomerType,
  CustomerFilters,
  CreateCustomerRequest,
} from '@/types/sales';
import ImportModal from '@modules/common/ImportModal';

const MD_CUSTOMER_TYPE_OPTIONS: { value: CustomerType; label: string }[] = [
  { value: 'Company', label: 'Компанія' },
  { value: 'Individual', label: 'Фізична особа' },
  { value: 'Hospital', label: 'Лікарня' },
  { value: 'Clinic', label: 'Клініка' },
  { value: 'Distributor', label: 'Дистриб\'ютор' },
];

// customerType на сервері — довільний рядок, тому мапи індексуються string з фолбеком
const MD_TYPE_LABEL_MAP: Record<string, string> = {
  Company: 'Компанія',
  Individual: 'Фізична особа',
  Hospital: 'Лікарня',
  Clinic: 'Клініка',
  Distributor: 'Дистриб\'ютор',
};

const MD_TYPE_COLOR_MAP: Record<string, string> = {
  Company: 'blue',
  Individual: 'green',
  Hospital: 'purple',
  Clinic: 'cyan',
  Distributor: 'orange',
};

const MD_TYPE_FILTER_OPTIONS: { value: CustomerType | ''; label: string }[] = [
  { value: '', label: 'Всі типи' },
  ...MD_CUSTOMER_TYPE_OPTIONS,
];

const MD_CURRENCY_OPTIONS = [
  { value: 'UAH', label: 'UAH' },
  { value: 'USD', label: 'USD' },
  { value: 'EUR', label: 'EUR' },
];

/** Значення форми з картки клієнта (null з сервера → undefined для полів форми) */
function mdToFormValues(mdCustomer: CustomerDetail): CreateCustomerRequest {
  return {
    code: mdCustomer.code,
    name: mdCustomer.name,
    customerType: mdCustomer.customerType ?? undefined,
    legalName: mdCustomer.legalName ?? undefined,
    taxId: mdCustomer.taxId ?? undefined,
    vatNumber: mdCustomer.vatNumber ?? undefined,
    email: mdCustomer.email ?? undefined,
    phone: mdCustomer.phone ?? undefined,
    contactPerson: mdCustomer.contactPerson ?? undefined,
    paymentTermDays: mdCustomer.paymentTermDays,
    creditLimitAmount: mdCustomer.creditLimitAmount ?? undefined,
    creditLimitCurrency: mdCustomer.creditLimitCurrency ?? 'UAH',
    street: mdCustomer.street ?? undefined,
    city: mdCustomer.city ?? undefined,
    region: mdCustomer.region ?? undefined,
    postalCode: mdCustomer.postalCode ?? undefined,
    shippingStreet: mdCustomer.shippingStreet ?? undefined,
    shippingCity: mdCustomer.shippingCity ?? undefined,
    shippingRegion: mdCustomer.shippingRegion ?? undefined,
    shippingPostalCode: mdCustomer.shippingPostalCode ?? undefined,
    website: mdCustomer.website ?? undefined,
    notes: mdCustomer.notes ?? undefined,
  };
}

export default function CustomersPage() {
  const [mdSearch, setMdSearch] = useState('');
  const [mdTypeFilter, setMdTypeFilter] = useState<CustomerType | ''>('');
  const [mdActiveFilter, setMdActiveFilter] = useState<boolean | undefined>(undefined);
  const [mdPage, setMdPage] = useState(1);
  const [mdPageSize, setMdPageSize] = useState(20);
  const [mdIsModalOpen, setMdIsModalOpen] = useState(false);
  const [mdEditingCustomerId, setMdEditingCustomerId] = useState<string | null>(null);

  const [mdIsImportModalOpen, setMdIsImportModalOpen] = useState(false);
  const [mdForm] = Form.useForm();

  const mdFilters: CustomerFilters = useMemo(() => ({
    search: mdSearch || undefined,
    customerType: mdTypeFilter || undefined,
    isActive: mdActiveFilter,
    page: mdPage,
    pageSize: mdPageSize,
  }), [mdSearch, mdTypeFilter, mdActiveFilter, mdPage, mdPageSize]);

  const { data: mdCustomersData, isLoading: mdIsLoading } = useCustomers(mdFilters);
  const mdCreateCustomer = useCreateCustomer();
  const mdUpdateCustomer = useUpdateCustomer();
  const mdImportCustomers = useImportCustomers();

  // CustomerDto списку не містить більшості полів форми — редагуємо за карткою GET /customers/{id}
  const { data: mdCustomerDetail } = useCustomerById(mdEditingCustomerId ?? undefined);
  const mdEditingCustomer =
    mdCustomerDetail && mdCustomerDetail.id === mdEditingCustomerId ? mdCustomerDetail : undefined;
  const mdIsDetailLoading = !!mdEditingCustomerId && !mdEditingCustomer;

  useEffect(() => {
    if (mdIsModalOpen && mdEditingCustomer) {
      mdForm.setFieldsValue(mdToFormValues(mdEditingCustomer));
    }
  }, [mdIsModalOpen, mdEditingCustomer, mdForm]);

  const mdHandleOpenCreate = () => {
    mdForm.resetFields();
    setMdEditingCustomerId(null);
    setMdIsModalOpen(true);
  };

  const mdHandleOpenEdit = (mdRecord: CustomerListItem) => {
    mdForm.resetFields();
    setMdEditingCustomerId(mdRecord.id);
    setMdIsModalOpen(true);
  };

  const mdHandleSubmit = async () => {
    const mdValues = await mdForm.validateFields();
    const mdRequest: CreateCustomerRequest = {
      code: mdValues.code,
      name: mdValues.name,
      customerType: mdValues.customerType,
      legalName: mdValues.legalName,
      taxId: mdValues.taxId,
      vatNumber: mdValues.vatNumber,
      email: mdValues.email,
      phone: mdValues.phone,
      contactPerson: mdValues.contactPerson,
      paymentTermDays: mdValues.paymentTermDays ?? 30,
      creditLimitAmount: mdValues.creditLimitAmount ?? undefined,
      creditLimitCurrency:
        mdValues.creditLimitAmount != null ? mdValues.creditLimitCurrency ?? 'UAH' : undefined,
      street: mdValues.street,
      city: mdValues.city,
      region: mdValues.region,
      postalCode: mdValues.postalCode,
      shippingStreet: mdValues.shippingStreet,
      shippingCity: mdValues.shippingCity,
      shippingRegion: mdValues.shippingRegion,
      shippingPostalCode: mdValues.shippingPostalCode,
      website: mdValues.website,
      notes: mdValues.notes,
    };

    if (mdEditingCustomerId) {
      if (!mdEditingCustomer) return;
      await mdUpdateCustomer.mutateAsync({
        ...mdRequest,
        id: mdEditingCustomerId,
        // Без isActive UpdateCustomerCommand вважає клієнта активним — передаємо поточний стан
        isActive: mdEditingCustomer.isActive,
      });
    } else {
      await mdCreateCustomer.mutateAsync(mdRequest);
    }
    setMdIsModalOpen(false);
  };

  const mdColumns: ColumnsType<CustomerListItem> = [
    {
      title: 'Код',
      dataIndex: 'code',
      key: 'code',
      width: 100,
      sorter: (mdA, mdB) => mdA.code.localeCompare(mdB.code),
    },
    {
      title: 'Назва',
      dataIndex: 'name',
      key: 'name',
      ellipsis: true,
      sorter: (mdA, mdB) => mdA.name.localeCompare(mdB.name),
    },
    {
      title: 'Тип',
      dataIndex: 'customerType',
      key: 'customerType',
      width: 140,
      render: (mdValue?: string | null) =>
        mdValue ? (
          <Tag color={MD_TYPE_COLOR_MAP[mdValue]}>{MD_TYPE_LABEL_MAP[mdValue] ?? mdValue}</Tag>
        ) : (
          '—'
        ),
    },
    {
      title: 'ЄДРПОУ',
      dataIndex: 'taxId',
      key: 'taxId',
      width: 120,
      render: (mdValue?: string | null) => mdValue || '—',
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      width: 200,
      ellipsis: true,
      render: (mdValue?: string | null) => mdValue || '—',
    },
    {
      title: 'Телефон',
      dataIndex: 'phone',
      key: 'phone',
      width: 150,
      render: (mdValue?: string | null) => mdValue || '—',
    },
    {
      title: 'Кредитний ліміт',
      dataIndex: 'creditLimitAmount',
      key: 'creditLimitAmount',
      width: 160,
      align: 'right',
      render: (mdValue: number | null | undefined, mdRecord: CustomerListItem) =>
        mdValue != null
          ? `${mdValue.toLocaleString('uk-UA', { minimumFractionDigits: 2 })} ${mdRecord.creditLimitCurrency ?? ''}`.trim()
          : '—',
    },
    {
      title: 'Дії',
      key: 'actions',
      width: 80,
      render: (_: unknown, mdRecord: CustomerListItem) => (
        <Button
          type="link"
          size="small"
          icon={<EditOutlined />}
          onClick={(mdEvent) => {
            mdEvent.stopPropagation();
            mdHandleOpenEdit(mdRecord);
          }}
        >
          Змінити
        </Button>
      ),
    },
  ];

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <Typography.Title level={4} style={{ margin: 0 }}>
          Клієнти
        </Typography.Title>
        <Space>
          <Button
            icon={<UploadOutlined />}
            onClick={() => setMdIsImportModalOpen(true)}
          >
            Імпорт з Excel
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={mdHandleOpenCreate}>
            Новий клієнт
          </Button>
        </Space>
      </div>

      <Space wrap style={{ marginBottom: 16 }}>
        <Input.Search
          placeholder="Пошук за назвою або кодом..."
          allowClear
          onSearch={(mdValue) => {
            setMdSearch(mdValue);
            setMdPage(1);
          }}
          style={{ width: 250 }}
        />
        <Select
          options={MD_TYPE_FILTER_OPTIONS}
          value={mdTypeFilter}
          onChange={(mdValue) => {
            setMdTypeFilter(mdValue);
            setMdPage(1);
          }}
          style={{ width: 180 }}
        />
        <Space>
          <Typography.Text type="secondary">Тільки активні:</Typography.Text>
          <Switch
            checked={mdActiveFilter === true}
            onChange={(mdChecked) => {
              setMdActiveFilter(mdChecked ? true : undefined);
              setMdPage(1);
            }}
          />
        </Space>
      </Space>

      <Table<CustomerListItem>
        columns={mdColumns}
        dataSource={mdCustomersData?.items}
        rowKey="id"
        loading={mdIsLoading}
        pagination={{
          current: mdPage,
          pageSize: mdPageSize,
          total: mdCustomersData?.totalCount,
          showSizeChanger: true,
          showTotal: (mdTotal) => `Всього: ${mdTotal}`,
          onChange: (mdNewPage, mdNewPageSize) => {
            setMdPage(mdNewPage);
            setMdPageSize(mdNewPageSize);
          },
        }}
        size="middle"
      />

      <Modal
        title={mdEditingCustomerId ? 'Редагувати клієнта' : 'Новий клієнт'}
        open={mdIsModalOpen}
        onCancel={() => setMdIsModalOpen(false)}
        onOk={mdHandleSubmit}
        confirmLoading={mdCreateCustomer.isPending || mdUpdateCustomer.isPending}
        okButtonProps={{ disabled: mdIsDetailLoading }}
        okText={mdEditingCustomerId ? 'Зберегти' : 'Створити'}
        cancelText="Скасувати"
        width={720}
      >
        <Form form={mdForm} layout="vertical">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
            <Form.Item
              name="code"
              label="Код"
              rules={[{ required: true, message: 'Введіть код клієнта' }]}
            >
              <Input maxLength={50} disabled={!!mdEditingCustomerId} />
            </Form.Item>
            <Form.Item
              name="name"
              label="Назва"
              rules={[{ required: true, message: 'Введіть назву' }]}
            >
              <Input maxLength={200} />
            </Form.Item>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
            <Form.Item name="customerType" label="Тип">
              <Select allowClear options={MD_CUSTOMER_TYPE_OPTIONS} />
            </Form.Item>
            <Form.Item name="legalName" label="Юридична назва">
              <Input maxLength={200} />
            </Form.Item>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0 16px' }}>
            <Form.Item name="taxId" label="ЄДРПОУ">
              <Input maxLength={10} />
            </Form.Item>
            <Form.Item name="vatNumber" label="ІПН (платника ПДВ)">
              <Input maxLength={20} />
            </Form.Item>
            <Form.Item name="contactPerson" label="Контактна особа">
              <Input maxLength={100} />
            </Form.Item>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
            <Form.Item name="email" label="Email">
              <Input maxLength={100} />
            </Form.Item>
            <Form.Item name="phone" label="Телефон">
              <Input maxLength={30} />
            </Form.Item>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
            <Form.Item name="website" label="Веб-сайт">
              <Input maxLength={200} />
            </Form.Item>
            <Form.Item name="paymentTermDays" label="Термін оплати (днів)">
              <InputNumber min={0} max={365} style={{ width: '100%' }} />
            </Form.Item>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '0 16px' }}>
            <Form.Item name="creditLimitAmount" label="Кредитний ліміт">
              <InputNumber min={0} precision={2} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="creditLimitCurrency" label="Валюта ліміту" initialValue="UAH">
              <Select options={MD_CURRENCY_OPTIONS} />
            </Form.Item>
          </div>
          <Typography.Text strong>Юридична адреса</Typography.Text>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: '0 16px', marginTop: 8 }}>
            <Form.Item name="street" label="Вулиця">
              <Input maxLength={200} />
            </Form.Item>
            <Form.Item name="city" label="Місто">
              <Input maxLength={100} />
            </Form.Item>
            <Form.Item name="region" label="Область">
              <Input maxLength={100} />
            </Form.Item>
            <Form.Item name="postalCode" label="Індекс">
              <Input maxLength={10} />
            </Form.Item>
          </div>
          <Typography.Text strong>Адреса доставки</Typography.Text>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: '0 16px', marginTop: 8 }}>
            <Form.Item name="shippingStreet" label="Вулиця">
              <Input maxLength={200} />
            </Form.Item>
            <Form.Item name="shippingCity" label="Місто">
              <Input maxLength={100} />
            </Form.Item>
            <Form.Item name="shippingRegion" label="Область">
              <Input maxLength={100} />
            </Form.Item>
            <Form.Item name="shippingPostalCode" label="Індекс">
              <Input maxLength={10} />
            </Form.Item>
          </div>
          <Form.Item name="notes" label="Примітки">
            <Input.TextArea rows={2} maxLength={2000} />
          </Form.Item>
        </Form>
      </Modal>

      <ImportModal
        open={mdIsImportModalOpen}
        title="Імпорт клієнтів з Excel"
        onClose={() => setMdIsImportModalOpen(false)}
        onImport={(mdFile) => mdImportCustomers.mutateAsync(mdFile)}
        isPending={mdImportCustomers.isPending}
      />
    </>
  );
}
